import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import {
    BodyTextMedium,
    Button,
    ButtonGroup,
    Form,
    FormInputText,
    HeadingText50,
    pxToRem,
    useForm,
} from '@modernhealth/vital-components';
import { InternalLinkButton } from 'src/shared/components/InternalLinkButton';

import { userAuthenticator } from 'src/config/rootService';
import logoImg from 'src/public/img/mh-logo-text-modern.svg';
import { MemberAccessFlowResponse } from 'src/modules/auth/models/auth';
import { hexToRgba } from 'src/shared/helpers/hexToRgba';
import { color, font } from 'src/shared/styles/variables';
import { isEmailValid } from 'src/shared/helpers/emailValidator';

interface RegistrationErrorProps {
    displayError: boolean;
}
const ErrorContainer = styled.div<RegistrationErrorProps>(({ displayError }) => ({
    display: displayError ? 'inline' : 'none',
    borderRadius: pxToRem(4),
    backgroundColor: `${hexToRgba(color.cornflowerBlueDark, 0.1)}`,
    width: '100%',
}));
const ErrorMessage = styled.div(
    {
        color: `${color.blueBayoux}`,
        lineHeight: pxToRem(24),
        padding: pxToRem(12),
    },
    font.roman(font.size.medium),
);
const BrandImage = styled.img({
    marginLeft: pxToRem(32),
    marginTop: pxToRem(20),
    width: '10em',
    cursor: 'pointer',
    marginRight: pxToRem(12),
});
const FormContainer = styled.div({
    maxWidth: pxToRem(500),
    width: '100%',
});
const Container = styled.div({
    display: 'flex',
    alignItems: 'start',
    paddingTop: pxToRem(112),
    paddingBottom: pxToRem(14),
    justifyContent: 'center',
    flexGrow: 1,
});

interface LocationProps {
    existingAccount: boolean;
}

interface Props {
    title: string;
    subtitle?: string;
    onFetch: (accessFlow: MemberAccessFlowResponse, email: string) => void;
}

export const AccessFlowForm: React.FC<Props> = ({ title, subtitle, onFetch }) => {
    const { t } = useTranslation();
    const location = useLocation<LocationProps>();
    const existingAccount = location.state?.existingAccount;

    const [errorTransKey, setErrorTransKey] = useState<string>('');
    const [fetchingAccessFlowInfo, setFetchingAccessFlowInfo] = useState<boolean>(false);

    const loginFormDefinition = useForm();
    const email = loginFormDefinition.watch('email');

    const displayError = !!errorTransKey && errorTransKey.length > 0;

    const handleSubmit = async () => {
        try {
            setErrorTransKey('');
            setFetchingAccessFlowInfo(true);
            const accessFlow = await userAuthenticator.checkMemberAccessFlow(email);
            setFetchingAccessFlowInfo(false);
            onFetch(accessFlow, email);
        } catch (error) {
            setFetchingAccessFlowInfo(false);

            if (error instanceof Error) {
                setErrorTransKey(error.message);
            }
        }
    };

    useEffect(() => {
        if (existingAccount) {
            setErrorTransKey(t('auth:login_form.already_have_account'));
        }
    }, [existingAccount, setErrorTransKey, t]);

    return (
        <>
            <BrandImage
                src={logoImg}
                onClick={() => {
                    window.location.href = 'https://www.modernhealth.com';
                }}
            />
            <Container>
                <FormContainer>
                    <HeadingText50 marginBottom={!!subtitle ? 0 : 24}>{title}</HeadingText50>

                    {!!subtitle && (
                        <BodyTextMedium marginTop={16} marginBottom={24}>
                            {subtitle}
                        </BodyTextMedium>
                    )}

                    <Form onSubmit={handleSubmit} definition={loginFormDefinition}>
                        <ErrorContainer displayError={displayError}>
                            <ErrorMessage>{t(errorTransKey)}</ErrorMessage>
                        </ErrorContainer>

                        <FormInputText
                            name="email"
                            label={t('auth:common_form_inputs.labels.email')}
                            trim
                        />

                        <ButtonGroup>
                            <InternalLinkButton to="/" variant="secondary" size="large">
                                {t('auth:login_form.back')}
                            </InternalLinkButton>
                            <Button
                                type="submit"
                                size="large"
                                busy={fetchingAccessFlowInfo}
                                disabled={!isEmailValid(email)}
                            >
                                {t('auth:login_form.continue')}
                            </Button>
                        </ButtonGroup>
                    </Form>
                </FormContainer>
            </Container>
        </>
    );
};
